/**
 * School settings.
 *
 * Four tabs, because these are four different jobs done by the same person at
 * different times: the school's own details, who is at the school, how papers
 * look, and what AI costs.
 *
 * Ordered the way a school is set up rather than the way the code grew: you name
 * the school, then invite the people, then decide how papers look, and only then
 * think about AI. "School details" is therefore also the landing tab.
 *
 * The people tab is the one that did not exist at all before: an administrator
 * could not add a colleague, so a school had exactly the accounts a seed script
 * made.
 */

import { useState } from 'react'
import type { ReactNode } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../../lib/api/client'
import type { Role } from '../../lib/api/client'
import { Button, Field, Input, PageHeader, Pill, Spinner, useTabList } from '../../components/ui'
import { Icon } from '../../components/icons'
import { Picture } from '../../components/Picture'
import { toast } from '../../components/toast'
import { SchoolAiSettingsPanel } from './AiSettings'
import { Templates } from './Templates'
import { People } from './People'
import { SchoolRetentionSettings } from '../retention/RetentionSettings'
import { SchoolEmailSettings } from '../email/SchoolEmailSettings'
import { AuditLog } from '../audit/AuditLog'
import './school.css'

type Tab = 'headers' | 'people' | 'details' | 'ai' | 'retention' | 'email' | 'audit'

// The keys are part of the URL (?tab=headers), so they stay put even where the
// label has moved on - an existing link must not break to rename a tab.
const TABS: [Tab, string][] = [
  ['details', 'School details'],
  ['people', 'People & roles'],
  ['headers', 'Paper settings'],
  ['retention', 'Retention'],
  ['email', 'Email'],
  ['ai', 'AI & billing'],
  ['audit', 'Audit log'],
]

export function Settings({
  schoolId,
  title = 'School settings',
  description,
  hideHeading = false,
  platformAiControl,
  platformEmailControl,
}: {
  schoolId?: string
  title?: string
  description?: string
  hideHeading?: boolean
  /** The site administrator's own AI panel, shown inside Which features.
   *  Passed in rather than imported, so this screen stays the same for a school
   *  administrator - who must not be handed the platform's controls just because
   *  the two now share a tab. */
  platformAiControl?: ReactNode
  /** The site administrator's email grants, shown above the Email tab. Same
   *  injection as platformAiControl, and for the same reason. */
  platformEmailControl?: ReactNode
}) {
  const tabs = useTabList()
  // Addressable, so something elsewhere can send an administrator to the right
  // tab. A link promising "Invite a colleague" that lands on Paper headers has
  // not done what it said.
  const [params, setParams] = useSearchParams()
  const requested = params.get('tab') as Tab | null
  const [fallback, setFallback] = useState<Tab>('details')
  const tab: Tab = requested && TABS.some(([key]) => key === requested) ? requested : fallback

  const setTab = (next: Tab) => {
    setFallback(next)
    const updated = new URLSearchParams(params)
    updated.set('tab', next)
    setParams(updated, { replace: true })
  }

  return (
    <div className="tc-page tc-settings">
      {!hideHeading && (
        <PageHeader
          eyebrow="School administration"
          title={title}
          icon={<Icon.settings size={20} />}
          description={description ?? 'Manage your school identity, people, paper settings, retention, and AI access.'}
        />
      )}

      <div className="tc-settings-tabs">
        <div className="tc-tabs" ref={tabs.ref} onKeyDown={tabs.onKeyDown} role="tablist" aria-label="School settings sections">
          {TABS.map(([key, label]) => (
            <button
              key={key}
              id={`tc-${key}-tab`}
              role="tab"
              aria-controls={`tc-${key}-panel`}
              aria-selected={tab === key}
              className={`tc-tab ${tab === key ? 'is-active' : ''}`}
              onClick={() => setTab(key)}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      <div
        id={`tc-${tab}-panel`}
        className="tc-settings-content"
        role="tabpanel"
        aria-labelledby={`tc-${tab}-tab`}
      >
        {tab === 'headers' && <Templates schoolId={schoolId} />}
        {tab === 'people' && <People schoolId={schoolId} />}
        {tab === 'details' && <SchoolDetails schoolId={schoolId} />}
        {tab === 'retention' && <SchoolRetentionSettings schoolId={schoolId} />}
        {tab === 'email' && (
          <div className="tc-email-sections">
            {platformEmailControl}
            <SchoolEmailSettings schoolId={schoolId} />
          </div>
        )}
        {tab === 'ai' && (
          <SchoolAiSettingsPanel schoolId={schoolId} platformAiControl={platformAiControl} />
        )}
        {tab === 'audit' && <AuditLog schoolId={schoolId} embedded />}
      </div>
    </div>
  )
}

function SchoolDetails({ schoolId }: { schoolId?: string }) {
  const queryClient = useQueryClient()
  const schoolKey = ['school', schoolId ?? 'current']
  const school = useQuery({
    queryKey: schoolKey,
    queryFn: () => api.school(schoolId),
  })
  const [draft, setDraft] = useState<Record<string, string> | null>(null)

  const save = useMutation({
    mutationFn: () => api.updateSchool(draft ?? {}, schoolId),
    onSuccess: (updated) => {
      queryClient.setQueryData(schoolKey, updated)
      queryClient.invalidateQueries({ queryKey: ['platform', 'schools'] })
      setDraft(null)
      toast('School details saved.')
    },
    onError: (error: Error) => toast(error.message, 'error'),
  })
  const refreshSchoolIdentity = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['school'] }),
      queryClient.invalidateQueries({ queryKey: ['platform', 'schools'] }),
      queryClient.invalidateQueries({ queryKey: ['templates', schoolId ?? 'current'] }),
    ])
  }
  const uploadLogo = useMutation({
    mutationFn: (file: File) => api.uploadSchoolLogo(file, schoolId),
    onSuccess: async (updated) => {
      queryClient.setQueryData(schoolKey, updated)
      await refreshSchoolIdentity()
      toast('School logo updated.')
    },
    onError: (error: Error) => toast(error.message, 'error'),
  })
  const clearLogo = useMutation({
    mutationFn: () => api.clearSchoolLogo(schoolId),
    onSuccess: async (updated) => {
      queryClient.setQueryData(schoolKey, updated)
      await refreshSchoolIdentity()
      toast('School logo removed.')
    },
    onError: (error: Error) => toast(error.message, 'error'),
  })

  if (school.isLoading) return <Spinner label="Loading school details" />
  if (!school.data) return null

  const value = draft ?? {
    name: school.data.name,
    branch: school.data.branch,
    phone: school.data.phone,
    email: school.data.email,
  }
  const set = (key: string, next: string) => setDraft({ ...value, [key]: next })

  return (
    <div className="tc-school-details">
      <section className="tc-school-details-card" aria-labelledby="tc-school-details-title">
        <header className="tc-school-details-head">
          <div>
            <h2 id="tc-school-details-title">School profile</h2>
            <p>The identity and contact details people see across TeachCraftAI.</p>
          </div>
          <Pill tone="muted">School-wide</Pill>
        </header>

        <div className="tc-school-details-body">
          <section className="tc-school-logo-setting" aria-labelledby="tc-school-logo-title">
            <span className="tc-school-logo-preview" aria-hidden="true">
              {school.data.logo_image_id ? (
                <Picture id={school.data.logo_image_id} />
              ) : (
                <Icon.pictures size={24} />
              )}
            </span>
            <div>
              <strong id="tc-school-logo-title">School logo</strong>
              <span>Used beside the school name and on the default paper header.</span>
            </div>
            <div className="tc-school-logo-actions">
              <label className="tc-file-button">
                {uploadLogo.isPending
                  ? 'Uploading…'
                  : school.data.logo_image_id
                    ? 'Replace logo'
                    : 'Upload logo'}
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  disabled={uploadLogo.isPending}
                  onChange={(event) => {
                    const file = event.target.files?.[0]
                    if (file) uploadLogo.mutate(file)
                    event.currentTarget.value = ''
                  }}
                />
              </label>
              {school.data.logo_image_id && (
                <Button
                  variant="danger"
                  disabled={clearLogo.isPending}
                  onClick={() => clearLogo.mutate()}
                >
                  Remove
                </Button>
              )}
            </div>
          </section>

          <div className="tc-school-details-fields">
            <div className="tc-school-primary-fields">
              <Field label="School name" hint="Shown in the application and school directory.">
                <Input value={value.name} onChange={(event) => set('name', event.target.value)} />
              </Field>
              <Field label="Branch" hint="For example: North Campus or Main Branch.">
                <Input value={value.branch} onChange={(event) => set('branch', event.target.value)} />
              </Field>
            </div>

            <div className="tc-school-details-subhead">
              <strong>Contact details</strong>
              <span>Used by platform administrators when they need to contact this school.</span>
            </div>
            <div className="tc-two-up">
              <Field label="Phone">
                <Input value={value.phone} onChange={(event) => set('phone', event.target.value)} />
              </Field>
              <Field label="Email">
                <Input value={value.email} onChange={(event) => set('email', event.target.value)} />
              </Field>
            </div>
          </div>
        </div>

        <footer className="tc-school-details-actions">
          <Button variant="secondary" disabled={!draft || save.isPending} onClick={() => setDraft(null)}>
            Discard changes
          </Button>
          <Button variant="primary" disabled={!draft || save.isPending} onClick={() => save.mutate()}>
            {save.isPending ? 'Saving…' : 'Save changes'}
          </Button>
        </footer>
      </section>
    </div>
  )
}

/** Roles arrive from the server already in teacher language, so no client invents
 *  its own wording - including for the owner role, which is not assignable but
 *  still has to be readable in a table. */
export function RolePills({ roles, labels }: { roles: string[]; labels: Record<string, Role> }) {
  return (
    <span className="tc-role-pills">
      {roles.map((role) => (
        <Pill key={role} tone={role === 'admin' ? 'secondary' : 'muted'}>
          {roleDisplayLabel(role, labels)}
        </Pill>
      ))}
    </span>
  )
}

export function roleDisplayLabel(role: string, labels: Record<string, Role>) {
  const consistent: Record<string, string> = {
    admin: 'Site administrator',
    school_admin: 'School administrator',
    print_admin: 'Print administrator',
    reviewer: 'Reviewer',
    teacher: 'Teacher',
  }
  return consistent[role] ?? labels[role]?.label ?? role.replaceAll('_', ' ')
}
